import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CosmicClient from './CosmicClient';
import AddOpModal from './AddOpModal';
import WhatsAppPanel from './WhatsAppPanel';
import OpDetailPanel from './OpDetailPanel';
import ClientOpsTable, { GRID_COLS } from './ClientOpsTable';
import CosmicPipelinePanel from './CosmicPipelinePanel';
import AddClientWizardRow from './AddClientWizardRow';
import TabPermissionsDropdown from './TabPermissionsDropdown';
import { ChevronLeft, ChevronRight, ChevronDown, Search, Plus, X } from 'lucide-react';
import ReactDOM from 'react-dom';
import { useDragResize } from '../../../shared/hooks/useDragResize';
import { getErrorMessage } from '../../../shared/errors/getErrorMessage';
import {
  useCreateUser, useTabPermissions, useTogglePermission,
} from '../hooks/useClientData';
import { useClientOps, useCreateOp, useUpdateOp } from '../hooks/useClientOps';
import { useAddClientWizard } from '../hooks/useAddClientWizard';
import {
  useCosmicClients, useCosmicHistory, useAdintelliDashboard, useCreateClientNotification,
  useLiveBenefitsPatch, cosmicClientsQueryKey, EMPTY_ADINTELLI,
} from '../hooks/useCosmicClients';
import { useAllUsersIncludingArchived } from '../../../shared/hooks/useAllUsers';
import { generateSecurePassword } from '../../../shared/lib/generatePassword';
import './ClientsView.css';
import { PRODUCT_COLORS, TABS } from '../lib/constants';

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: deriva la pestaña activa y el panel seleccionado de la URL / del `overlayOp` que llega por prop.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const TIME_RANGES = [
  { value: 'todo',    labelKey: 'timeRanges.all' },
  { value: 'hoy',     labelKey: 'timeRanges.today' },
  { value: '15-dias', labelKey: 'timeRanges.15days' },
  { value: '1-mes',   labelKey: 'timeRanges.1month' },
  { value: '3-meses', labelKey: 'timeRanges.3months' },
  { value: '6-meses', labelKey: 'timeRanges.6months' },
  { value: '1-ano',   labelKey: 'timeRanges.1year' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Fase 5.15 -- presentacional: `toast`, `onNavigate` (antes `useNavigate`), `pathname` (antes
// `useLocation().pathname`), `spacePrefix` + `clienteShortId` (antes `useParams`) llegan por
// prop del container (`ClientsContainer`). El prefijo `/:spaceSlug` importa porque todas las
// rutas de este componente viven anidadas bajo `<Route path="/:spaceSlug">` (App.jsx) --
// navegar a un path absoluto sin él cae en el catch-all `/database/*` y pierde la ruta (bug
// E2E 2026-07-15).
export default function ClientsView({ user, overlayOp, onCloseOverlay, overlayWidth, setOverlayWidth, toast, onNavigate, pathname = '', spacePrefix = '', clienteShortId, urlTab, urlNodeId }) {
  const { t } = useTranslation('client');
  const navigate = onNavigate;

  const getInitialTab = useCallback(() => {
    const p = pathname;
    if (p.startsWith(`${spacePrefix}/database/clientes/fact`)) return 'facturacion';
    if (p.startsWith(`${spacePrefix}/database/clientes/otc`)) return 'otc';
    if (p.startsWith(`${spacePrefix}/database/tpv`) || p.startsWith(`${spacePrefix}/database/clientes/tpv`)) return 'terminales';
    if (p.startsWith(`${spacePrefix}/database/clientes/paylink`)) return 'links_pago';
    if (p.startsWith(`${spacePrefix}/database/clientes/wallet`)) return 'wallet';
    if (p.startsWith(`${spacePrefix}/database/clientes/soft`)) return 'software';
    if (p.startsWith(`${spacePrefix}/database/cosmic`) || p.startsWith(`${spacePrefix}/database/clientes/cosmic`)) return 'telefonica';
    return 'todos';
  }, [pathname, spacePrefix]);

  const getBasePath = useCallback((tab) => {
    if (tab === 'facturacion') return `${spacePrefix}/database/clientes/fact`;
    if (tab === 'otc') return `${spacePrefix}/database/clientes/otc`;
    if (tab === 'terminales') return `${spacePrefix}/database/clientes/tpv`;
    if (tab === 'links_pago') return `${spacePrefix}/database/clientes/paylink`;
    if (tab === 'wallet') return `${spacePrefix}/database/clientes/wallet`;
    if (tab === 'software') return `${spacePrefix}/database/clientes/soft`;
    if (tab === 'telefonica') return `${spacePrefix}/database/cosmic`;
    return `${spacePrefix}/database/clientes`;
  }, [spacePrefix]);

  const [activeTab, setActiveTab] = useState(getInitialTab());

  useEffect(() => {
    if (!overlayOp) {
      setActiveTab(getInitialTab());
    }
  }, [pathname, overlayOp, getInitialTab]);
  const [timeRange, setTimeRange] = useState('todo');
  const [search,    setSearch]    = useState('');
  const [selectedCosmicNode, setSelectedCosmicNode] = useState(null);
  const [cosmicViewMode, setCosmicViewMode] = useState('activas');
  const [cosmicGlobalAction, setCosmicGlobalAction] = useState(null);
  const [showCosmicWhats, setShowCosmicWhats] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAdd,   setShowAdd]   = useState(false);
  const [date,      setDate]      = useState(new Date().toISOString().split('T')[0]);

  const isTelefonica = activeTab === 'telefonica';
  const trimmedSearch = search.trim() || undefined;

  // Pipeline normal (todo lo que no es Cosmic) -- reemplaza el `fetchOps`/`useState(ops)` de
  // antes; React Query re-pide solo cuando `activeTab`/`timeRange`/`search` cambian, igual que
  // el `useCallback` que reemplaza.
  const opsQuery = useClientOps({
    category: activeTab !== 'todos' ? activeTab : undefined,
    range: timeRange !== 'todo' ? timeRange : undefined,
    q: trimmedSearch,
    enabled: !isTelefonica,
  });
  const cosmicQuery = useCosmicClients({
    status: cosmicViewMode,
    search: trimmedSearch,
    page: currentPage,
    enabled: isTelefonica && cosmicViewMode !== 'historial',
  });
  const cosmicHistoryQuery = useCosmicHistory({
    search: trimmedSearch,
    page: currentPage,
    enabled: isTelefonica && cosmicViewMode === 'historial',
  });
  const adintelliQuery = useAdintelliDashboard({ enabled: isTelefonica });
  const createNotificationMutation = useCreateClientNotification();
  const liveBenefitsPatch = useLiveBenefitsPatch();

  // `useMemo` acá, no un `const` derivado plano -- sin esto, la rama `?? []` crea un array
  // nuevo en cada render mientras la query está en `loading`, e invalida el `useMemo` de
  // `existingIndustries` (que depende de `ops`) en cada render de balde.
  const ops = useMemo(() => (
    isTelefonica
      ? (cosmicViewMode === 'historial' ? [] : (cosmicQuery.data?.clients ?? []))
      : (opsQuery.data ?? [])
  ), [isTelefonica, cosmicViewMode, cosmicQuery.data, opsQuery.data]);
  const historyOps = cosmicHistoryQuery.data?.history ?? [];
  const opsTotal = isTelefonica
    ? (cosmicViewMode === 'historial' ? (cosmicHistoryQuery.data?.total ?? 0) : (cosmicQuery.data?.total ?? 0))
    : 0;
  const cosmicCounts = cosmicQuery.data?.counts ?? { activas: 0, inventario: 0, suspendidas: 0 };
  const adintelliData = adintelliQuery.data ?? EMPTY_ADINTELLI;
  const loading = isTelefonica
    ? (cosmicViewMode === 'historial' ? cosmicHistoryQuery.isLoading : cosmicQuery.isLoading)
    : opsQuery.isLoading;

  const activeCosmicNode = selectedCosmicNode || (clienteShortId ? ops.find(o => o.line === clienteShortId) : null);

  const [cosmicRightWidth, setCosmicRightWidth] = useState(600);
  // Mismo hook compartido que `startResizeRight`/`startResizeLeft` de `OpDetailPanel`
  // — este sitio era un duplicado literal de esa lógica.
  const startResizeCosmicRightDrag = useDragResize({
    onMove: ({ moveEvent }) => {
      const newWidth = Math.max(320, Math.min(window.innerWidth - 100, window.innerWidth - moveEvent.clientX));
      setCosmicRightWidth(newWidth);
      return newWidth;
    },
  });
  const startResizeCosmicRight = (e) => startResizeCosmicRightDrag(e, cosmicRightWidth);
  const [selected,  setSelected]  = useState(overlayOp || null);

  const { data: allUsers = [] } = useAllUsersIncludingArchived();
  const createUserMutation = useCreateUser();
  const { data: tabPermissions = [] } = useTabPermissions();
  const togglePermMutation = useTogglePermission();
  const createOpMutation = useCreateOp();
  const updateOpMutation = useUpdateOp();
  const [showPermsDrop, setShowPermsDrop] = useState(false);
  const [permsSearch, setPermsSearch] = useState('');
  const permsRef = useRef(null);
  const [showProdDrop, setShowProdDrop] = useState(false);
  const prodRef = useRef(null);
  // 04-enforce.md 4.1 -- el wizard inline de "agregar cliente" (~250 líneas operativas de estado
  // + handlers) vive ahora en `useAddClientWizard`. El JSX sigue en `AddClientWizardRow`.
  const wizard = useAddClientWizard({
    allUsers, ops, user, spacePrefix,
    createUserMutation, createOpMutation, createNotificationMutation,
    toast, translate: t,
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (permsRef.current && !permsRef.current.contains(e.target)) {
        setShowPermsDrop(false);
      }
      if (prodRef.current && !prodRef.current.contains(e.target)) {
        setShowProdDrop(false);
      }
    };
    if (showPermsDrop || showProdDrop) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPermsDrop, showProdDrop]);

  const handleTogglePerm = async (userId) => {
    const isAct = tabPermissions.some(p => p.tab_id === activeTab && p.user_id === userId);
    const tabsToUpdate = activeTab === 'todos' ? TABS.map(t => t.id) : [activeTab];

    try {
      await togglePermMutation.mutateAsync({ tab_ids: tabsToUpdate, user_id: userId, active: !isAct });
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  // Antes "admin" acá era
  // IDs harcodeados (1, 2) + `nick.includes('admin')` — un gate puramente cosmético, sin
  // relación con el rol real del usuario. Ahora usa `user.role` de verdad (mismo criterio que
  // `User.isAdmin()` en el backend: admin/super-admin/sub-admin).
  const isAdminRole = (u) => Array.isArray(u?.role) && ['admin', 'super-admin', 'sub-admin'].some((r) => u.role.includes(r));
  // Recorría `allUsers` de nuevo en cada render (componente con ~30 useState, cualquier
  // tecleo/estado no relacionado disparaba el recálculo).
  const authorizedUsers = useMemo(
    () => allUsers.filter(u => isAdminRole(u) || user?.id === u.id || tabPermissions.some(p => p.tab_id === activeTab && p.user_id === u.id)),
    [allUsers, tabPermissions, activeTab, user?.id]
  );
  const userHasAccessToTab = isAdminRole(user) || tabPermissions.some(p => p.tab_id === activeTab && p.user_id === user?.id);
  // Antes
  // se recalculaba recorriendo `ops` completo dentro del `.map()` de filas — idéntico para
  // todas las filas, era O(n²) en cada render. Declarado antes del `return` condicional de
  // más abajo (modo overlay) — los hooks no pueden quedar después de un return temprano.
  const existingIndustries = useMemo(
    () => Array.from(new Set(ops.map(o => o.industry).filter(Boolean))),
    [ops]
  );
  // Filtro+sort del dropdown de permisos, antes recalculado en cada render sin
  // `useMemo` (cualquier tecleo no relacionado a `permsSearch` lo disparaba igual).
  const sortedPermsDropdownUsers = useMemo(
    () => allUsers.filter(u => (u.nick || '').toLowerCase().includes(permsSearch.toLowerCase())).sort((a, b) => {
      const isActA = authorizedUsers.some(au => au.id === a.id);
      const isActB = authorizedUsers.some(au => au.id === b.id);
      if (isActA && !isActB) return -1;
      if (!isActA && isActB) return 1;
      return (a.nick || '').localeCompare(b.nick || '');
    }),
    [allUsers, permsSearch, authorizedUsers]
  );

  // Sync overlayOp (from URL shortId) into selected panel
  React.useEffect(() => { if (overlayOp) setSelected(overlayOp); }, [overlayOp]);

  // ── URL sync helpers ──────────────────────────────────────────────
  const openPanel = (op) => {
    setSelected(op);
    navigate(getBasePath(activeTab) + '/' + (op.short_id || op.id));
  };

  const handlePanelTabChange = (tabId) => {
    if (!selected) return;
    const shortId = selected.short_id || selected.id;
    // Antes traducía tabId a un slug via un ternario que solo conocía 'proceso'/'config' --
    // cualquier otra pestaña (incluida 'comments', agregada 2026-07-15) caía por defecto a
    // 'flow', pisando la URL correcta que OpDetailPanel.changeTab() ya había armado. tabId ya
    // es el slug real (coincide 1:1 con los ids de OpDetailPanel.TABS), no hace falta traducir.
    navigate(getBasePath(activeTab) + '/' + shortId + '/' + tabId, { replace: true });
  };

  const closePanel = () => {
    setSelected(null);
    navigate(getBasePath(activeTab), { replace: true });
    onCloseOverlay?.();
  };

  // `ops`/`historyOps`/`opsTotal`/`cosmicCounts`/`adintelliData`/`loading` ahora vienen de
  // `opsQuery`/`cosmicQuery`/`cosmicHistoryQuery`/`adintelliQuery` (arriba) -- React Query ya
  // vuelve a pedir cuando `activeTab`/`timeRange`/`search`/`cosmicViewMode`/`currentPage`
  // cambian, no hace falta el `useCallback`+`useEffect` manual que había acá. Errores de carga:
  // antes un `catch(e){console.error(e)}` silencioso -- ahora expuestos vía
  // `opsQuery.error`/`cosmicQuery.error`/etc (`isError`), ver el efecto de abajo.
  useEffect(() => {
    const err = opsQuery.error || cosmicQuery.error || cosmicHistoryQuery.error || adintelliQuery.error;
    if (err) toast.error(getErrorMessage(err));
  }, [opsQuery.error, cosmicQuery.error, cosmicHistoryQuery.error, adintelliQuery.error, toast]);

  useEffect(() => {
    if (!isTelefonica || cosmicViewMode === 'historial' || !ops || ops.length === 0) return;

    const missingLines = ops.filter(o => o.recharge_date === null).map(o => o.line);
    if (missingLines.length === 0) return;

    const queryKey = cosmicClientsQueryKey({ status: cosmicViewMode, search: trimmedSearch, page: currentPage });
    const timer = setTimeout(() => {
      liveBenefitsPatch.mutate({ lines: missingLines, queryKey });
    }, 500); // Small debounce

    return () => clearTimeout(timer);
  }, [ops, isTelefonica, currentPage, cosmicViewMode, trimmedSearch, liveBenefitsPatch]);

  const shiftDate = (dir) => {
    const d = new Date(date);
    d.setDate(d.getDate() + dir);
    setDate(d.toISOString().split('T')[0]);
  };

  // ── Wrappers para las hojas presentacionales (ClientOpsTable/CosmicPipelinePanel) --
  // reemplazan las llamadas directas a `updateOpMutation`/`navigate`/setters que vivían inline
  // en el JSX de antes.
  const handleUpdateProduct = (op, v) => updateOpMutation.mutateAsync({ opId: op.id, fields: { product: v } });
  const handleUpdateIndustry = (op, v) => updateOpMutation.mutateAsync({ opId: op.id, fields: { industry: v } });
  const handleArchive = (op) => {
    toast.prompt(t('table.archiveConfirm'), {
      confirmText: 'Archivar',
      onConfirm: () => updateOpMutation.mutateAsync({ opId: op.id, fields: { archived: true } }).catch(() => {}),
    });
  };
  const handleAddClick = () => {
    if (activeTab === 'terminales') wizard.startWizard();
    else setShowAdd(true);
  };
  const handleSetCosmicViewMode = (mode) => { setCosmicViewMode(mode); setCurrentPage(1); };
  const handleGlobalCosmicAction = (action) => { setCosmicGlobalAction(action); setSelectedCosmicNode(null); };
  const handleLineClick = (line) => navigate(`/database/cosmic/${line}`);

  // ── Overlay mode: render only the detail panel ──────────────────
  if (overlayOp) {
    const op = selected || overlayOp;
    return (
      <OpDetailPanel
        op={op}
        currentUserId={user?.id}
        currentUserNick={user?.nick}
        currentUserImg={(user?.['link-profile-img-thumb'] || user?.['link-profile-img'])}
        onClose={closePanel}
        onSaved={() => {}}
        onTabChange={handlePanelTabChange}
        overlayWidth={overlayWidth}
        setOverlayWidth={setOverlayWidth}
        onNavigate={navigate}
        spacePrefix={spacePrefix}
        urlTab={urlTab}
        urlNodeId={urlNodeId}
        toast={toast}
      />
    );
  }

  return (
    <div className="client-view">
      {/* Header */}
      <div className="client-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="client-breadcrumb" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {t('breadcrumb.path')}

          <div ref={prodRef} style={{ position: 'relative' }}>
            <div 
              onClick={() => setShowProdDrop(!showProdDrop)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-1) var(--space-3)',
                borderRadius: 'var(--radius-xl)',
                background: (PRODUCT_COLORS[activeTab === 'todos' ? 'todos' : activeTab] || 'rgba(255,255,255,0.2)') + '22',
                color: PRODUCT_COLORS[activeTab === 'todos' ? 'todos' : activeTab] || '#fff',
                border: `1px solid ${PRODUCT_COLORS[activeTab === 'todos' ? 'todos' : activeTab] || 'rgba(255,255,255,0.2)'}44`,
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              {TABS.find(tab => tab.id === activeTab)?.label || t('breadcrumb.all')}
              <ChevronDown size={12} />
            </div>

            {showProdDrop && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: 'var(--space-2)',
                background: '#16161a',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-2)',
                zIndex: 100,
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                minWidth: '150px'
              }}>
                {TABS.map(t => (
                  <div
                    key={t.id}
                    onClick={() => {
                      navigate(getBasePath(t.id));
                      setShowProdDrop(false);
                    }}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      color: activeTab === t.id ? (PRODUCT_COLORS[t.id] || '#fff') : 'rgba(255,255,255,0.5)',
                      background: activeTab === t.id ? (PRODUCT_COLORS[t.id] || 'rgba(255,255,255,0.2)') + '15' : 'transparent',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => { if (activeTab !== t.id) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                    onMouseLeave={e => { if (activeTab !== t.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {t.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <TabPermissionsDropdown
            permsRef={permsRef}
            allUsers={allUsers}
            authorizedUsers={authorizedUsers}
            showPermsDrop={showPermsDrop}
            onToggleDrop={() => setShowPermsDrop(!showPermsDrop)}
            permsSearch={permsSearch}
            onPermsSearchChange={setPermsSearch}
            sortedPermsDropdownUsers={sortedPermsDropdownUsers}
            isUserAdmin={(u) => isAdminRole(u) || user?.id === u.id}
            onTogglePerm={handleTogglePerm}
          />
          <div className="client-search-box">
            <Search size={13} />
            <input type="text" placeholder={t('table.searchPlaceholder')} aria-label={t('table.searchAriaLabel')}
              value={search} onChange={e => setSearch(e.target.value)} />
            {search && (
              <button style={{background:'none',border:'none',cursor:'pointer',padding:0,display:'flex'}}
                aria-label={t('table.clearSearchAriaLabel')}
                onClick={() => setSearch('')}>
                <X size={12} color="rgba(255,255,255,0.3)" />
              </button>
            )}
          </div>

          <div className="client-date-nav">
            <button className="client-nav-btn" aria-label={t('table.prevDateAriaLabel')} onClick={() => shiftDate(-1)}><ChevronLeft size={14} /></button>
            <select className="client-nav-select" aria-label={t('table.timeRangeAriaLabel')} value={timeRange} onChange={e => setTimeRange(e.target.value)}>
              {TIME_RANGES.map(r => <option key={r.value} value={r.value}>{t(r.labelKey)}</option>)}
            </select>
            <input type="date" className="client-date-input" aria-label={t('table.dateAriaLabel')} value={date} onChange={e => setDate(e.target.value)} />
            <button className="client-nav-btn" aria-label={t('table.nextDateAriaLabel')} onClick={() => shiftDate(1)}><ChevronRight size={14} /></button>
          </div>
        </div>
      </div>

      <div className="client-body">
        {/* Table */}
        <div className="client-table-wrapper">
          {activeTab !== 'telefonica' && (
            <ClientOpsTable
              userHasAccessToTab={userHasAccessToTab}
              loading={loading}
              ops={ops}
              search={search}
              existingIndustries={existingIndustries}
              onOpenPanel={openPanel}
              onUpdateProduct={handleUpdateProduct}
              onUpdateIndustry={handleUpdateIndustry}
              onArchive={handleArchive}
              onAddClick={handleAddClick}
              wizardRow={wizard.wizardActive && (
                <AddClientWizardRow
                  gridCols={GRID_COLS}
                  wizardStep={wizard.wizardStep}
                  wizardData={wizard.wizardData}
                  clientMatches={wizard.clientMatches}
                  clientActiveIdx={wizard.clientActiveIdx}
                  industryMatches={wizard.industryMatches}
                  industryActiveIdx={wizard.industryActiveIdx}
                  operMatches={wizard.operMatches}
                  operActiveIdx={wizard.operActiveIdx}
                  allUsers={allUsers}
                  clientInputRef={wizard.clientInputRef}
                  industryInputRef={wizard.industryInputRef}
                  transInputRef={wizard.transInputRef}
                  operInputRef={wizard.operInputRef}
                  onIndustryInputChange={wizard.handleIndustryInputChange}
                  onIndustryKeyDown={wizard.handleIndustryKeyDown}
                  onSelectIndustry={wizard.selectIndustry}
                  onClientInputChange={wizard.handleClientInputChange}
                  onClientKeyDown={wizard.handleClientKeyDown}
                  onSelectClient={wizard.selectClient}
                  onStartCreateClient={wizard.startCreateClient}
                  onNewClientNameChange={(v) => wizard.setWizardData(p => ({ ...p, new_client_name: v }))}
                  onNewClientEmailChange={(v) => wizard.setWizardData(p => ({ ...p, new_client_email: v }))}
                  onRegeneratePassword={() => wizard.setWizardData(p => ({ ...p, new_client_password: generateSecurePassword() }))}
                  onCreateClient={wizard.handleCreateClient}
                  onTransactionChange={(v) => wizard.setWizardData(prev => ({ ...prev, transaction: v }))}
                  onTransKeyDown={wizard.handleTransKeyDown}
                  onOperInputChange={wizard.handleOperInputChange}
                  onOperKeyDown={wizard.handleOperKeyDown}
                  onSelectOper={wizard.selectOper}
                  onGoToStep={wizard.setWizardStep}
                  onCancel={wizard.cancelWizard}
                />
              )}
            />
          )}

          {activeTab === 'telefonica' && (
            <CosmicPipelinePanel
              adintelliData={adintelliData}
              cosmicViewMode={cosmicViewMode}
              onSetCosmicViewMode={handleSetCosmicViewMode}
              cosmicCounts={cosmicCounts}
              historyOps={historyOps}
              ops={ops}
              opsTotal={opsTotal}
              currentPage={currentPage}
              onPrevPage={() => setCurrentPage(p => Math.max(1, p - 1))}
              onNextPage={() => setCurrentPage(p => Math.min(Math.ceil(opsTotal / 120), p + 1))}
              onGlobalAction={handleGlobalCosmicAction}
              onLineClick={handleLineClick}
            />
          )}
        </div>
      </div>

      {/* Add modal */}
      {showAdd && (
        <AddOpModal onClose={() => setShowAdd(false)} onSave={() => {}} user={user} />
      )}

      {(activeCosmicNode || cosmicGlobalAction) && activeTab === 'telefonica' && (
        <>
          <div className="op-detail-overlay" onClick={() => {
            setSelectedCosmicNode(null);
            setCosmicGlobalAction(null);
            setShowCosmicWhats(false);
            if (clienteShortId) navigate(`${spacePrefix}/database/cosmic`);
          }} />
          <CosmicClient
            op={activeCosmicNode}
            globalAction={cosmicGlobalAction}
            user={user}
            toast={toast}
            onClose={() => { 
              setSelectedCosmicNode(null); 
              setCosmicGlobalAction(null); 
              setShowCosmicWhats(false); 
              if (clienteShortId) navigate(`${spacePrefix}/database/cosmic`);
            }} 
            rightWidth={cosmicRightWidth}
            startResizeRight={startResizeCosmicRight}
            onToggleWhatsApp={() => setShowCosmicWhats(p => !p)}
          />
        </>
      )}
      
      {showCosmicWhats && ReactDOM.createPortal(
        <div 
          className="whats-panel" 
          style={{ 
            position: 'fixed',
            right: 600,
            top: 0,
            bottom: 0,
            width: '380px',
            zIndex: 1000000,
            background: '#1a1d24',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
            transition: 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <WhatsAppPanel onClose={() => setShowCosmicWhats(false)} toast={toast} />
        </div>,
        document.body
      )}
    </div>
  );
}
